/**
 * @file planChangeImpact.ts
 * @brief Before Save on Super Admin → Plans & seats: for every changed plan
 *        number, when it applies and who it reaches (B20 / W17)
 *
 * @details
 * DES 24-10-2026: billing is a trust area, so no plan change saves without
 * "Save will change: old → new", "Applies: …" and "Affects: N users,
 * M companies". The rule comes with each setting on GET /api/admin/plans
 * (`rule.appliesFrom`, BACKEND planSettingRules.mjs, enforced in billing);
 * the counts from GET /api/admin/plans/impact (read-only COUNTs).
 *
 * Until BACKEND B20 is deployed there is no rule and no impact endpoint
 * (404): "Applies: –" / "Affects: –", Save is not blocked on it, and no
 * number is ever made up. A count the server could not take (null) is "–".
 *
 * Direction is for the customer, the same as the server's changeDirection:
 * "up" = more generous (more minutes, longer history, lower price, fewer
 * minimum seats). Up applies at once; down waits (next period, new sign-ups,
 * new subscriptions), so nobody loses something mid-month.
 *
 * Pure (relative imports only) so node:test can load it.
 *
 * Related: components/PlanChangesBar.tsx, launchPlans.ts,
 *   BACKEND src/features/admin/planSettingRules.mjs, planImpact.mjs
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import type { FreeResetMode, PlanRule } from "./launchPlans.ts";

export type Direction = "up" | "down" | "same" | "changed";

/** BACKEND planSettingRules.mjs changeDirection, on stored values. */
export function directionOf(key: string, from: string | null | undefined, to: string | null | undefined): Direction {
  const a = from == null ? null : String(from).trim();
  const b = String(to ?? "").trim();
  if (a === b) return "same";
  let unlimited = (_n: number) => false;
  if (/HISTORY_DAYS$|EXPIRY_DAYS$|MAX_SEATS$/.test(key)) unlimited = (n) => n <= 0;
  else if (/SEARCH_LIMIT$|NOTES_MINUTES$/.test(key) && !/^FREE_/.test(key)) unlimited = (n) => n < 0;
  const fewerIsBetter = /MIN_SEATS$|_MINOR$/.test(key);
  const num = (v: string | null) => {
    const n = parseInt(String(v ?? ""), 10);
    if (!Number.isFinite(n)) return null;
    return unlimited(n) ? Infinity : n;
  };
  const x = num(a);
  const y = num(b);
  if (x == null || y == null) return "changed";
  if (x === y) return "same";
  return (fewerIsBetter ? y < x : y > x) ? "up" : "down";
}

export const isPriceKey = (key: string) => /^PLAN_(PRO|BUSINESS)_PRICE_(GBP|USD)(_ANNUAL)?_MINOR$/.test(key);

/** The line under every price change, true before and after B20. */
export const PRICE_KEEP_NOTE = "Existing subscribers keep their price.";

/**
 * "at once", "from each user's next billing period", "new subscriptions
 * only", "next session"… or "–" without a rule (backend before B20).
 */
export function appliesText(
  key: string,
  rule: PlanRule | null | undefined,
  direction: Direction,
  freeMode: FreeResetMode,
  /** B20: the Free reset mode changes in the same Save. */
  modeSwitch = false,
): string {
  if (!rule) return "–";
  // Saved with the switch, the Free numbers ARE the new mode's numbers: every
  // Free user gets them at once (one time: what is left of the new total).
  if (modeSwitch && /^FREE_PLAN_(NOTES_MINUTES|ASK_LIMIT|EXPIRY_DAYS)$/.test(key)) {
    return freeMode === "one_time" ? "at once, with the switch: the new total" : "at once, with the switch";
  }
  const free = /^FREE_/.test(key);
  const down = direction === "down";
  const either = direction === "changed";
  switch (rule.appliesFrom) {
    case "now":
      return "at once";
    case "up_now_down_next_period": {
      const later = free
        ? freeMode === "one_time" && key !== "FREE_PLAN_HISTORY_DAYS"
          ? "new sign-ups only"
          : "from the 1st of next month"
        : "from each user's next billing period";
      if (down) return later;
      return either ? `more: at once; less: ${later}` : "at once";
    }
    case "up_now_down_new_signups": {
      const later = /^FREE_INTERPRET_/.test(key) ? "new grants only" : "new sign-ups only";
      if (down) return later;
      return either ? `longer: at once; shorter: ${later}` : "at once";
    }
    case "new_subscriptions":
      return "new subscriptions only";
    case "next_session":
      return "next session";
    case "next_recording":
      return "next recording";
    case "seats":
      if (/MIN_SEATS$/.test(key)) return down ? "new plans and plan changes only" : "at once";
      return down ? "new plans and new members only" : "at once";
    default:
      return rule.appliesFrom;
  }
}

/** One count per key, as the impact endpoint sends it (null = not counted). */
export type ImpactCounts = Record<string, number | null>;

export type ImpactView =
  | { status: "loading" }
  | { status: "ok"; counts: ImpactCounts; note?: string | null }
  | { status: "missing" }
  | { status: "error"; message: string };

const n = (v: number) => v.toLocaleString("en-GB");
const plural = (v: number, one: string, many = `${one}s`) => `${n(v)} ${v === 1 ? one : many}`;
const count = (v: number | null | undefined, one: string, many?: string) =>
  v == null ? `– ${many ?? `${one}s`}` : plural(v, one, many);

/**
 * "Affects: 1,204 users", "3 subscriptions, 1 company", "–". The main
 * count; `extra` sentences come from extraImpact.
 */
export function affectsText(key: string, view: ImpactView | undefined): string {
  if (!view || view.status === "loading") return "counting…";
  if (view.status !== "ok") return "–";
  const c = view.counts;
  const parts: string[] = [];
  if ("subscriptions" in c) parts.push(count(c.subscriptions, "subscription"));
  if ("users" in c) parts.push(count(c.users, "user"));
  if ("usersLast30Days" in c) parts.push(`${count(c.usersLast30Days, "user")} in the last 30 days`);
  // Pro is one person: its "companies" is always 0, so it says nothing.
  if ("companies" in c && !/^PLAN_PRO_/.test(key)) parts.push(count(c.companies, "company", "companies"));
  if ("grants" in c) parts.push(`${count(c.grants, "free interpretation grant")}`);
  if (!parts.length && "notesLast30DaysLonger" in c) return "the next voice notes";
  return parts.length ? parts.join(", ") : "–";
}

/**
 * The key-specific counts that matter for the rule, in words. `freeMode`
 * is the mode the Free numbers will have after this Save; `freeTotal` the
 * Free minutes total after it (for a switch to one-time).
 */
export function extraImpact(
  key: string,
  view: ImpactView | undefined,
  opts: { freeMode: FreeResetMode; notesView?: ImpactView; freeTotal?: string | null; modeSwitch?: boolean } = {
    freeMode: "monthly",
  },
): string[] {
  if (!view || view.status !== "ok") return [];
  const c = view.counts;
  const out: string[] = [];
  // With a switch to one time in the same Save, the mode line says it once.
  if (key === "FREE_PLAN_NOTES_MINUTES" && !opts.modeSwitch) {
    if (opts.freeMode === "one_time" && c.usersOverInTotal != null) {
      out.push(`${plural(c.usersOverInTotal, "user has", "users have")} already used that many free minutes: no more for them.`);
    } else if (opts.freeMode === "monthly" && c.usersAlreadyOver != null && c.usersAlreadyOver > 0) {
      out.push(`${plural(c.usersAlreadyOver, "user is", "users are")} already past it this month: they keep this month.`);
    }
  }
  if (key === "FREE_PLAN_RESET_MODE") {
    // Counted against the total this Save sets, when the minutes change too.
    const nv = opts.notesView;
    const used =
      nv?.status === "ok" && opts.freeTotal ? nv.counts.usersOverInTotal : c.usersAlreadyUsedTotal;
    if (used != null && opts.freeMode === "one_time") {
      const total = opts.freeTotal ? `${opts.freeTotal} min` : "the total";
      out.push(`${plural(used, "user has", "users have")} already used ${total}: no more for them.`);
    }
  }
  if (c.companiesOutside != null && c.companiesOutside > 0) {
    out.push(`${plural(c.companiesOutside, "company", "companies")} outside the new number keep their seats.`);
  }
  if (c.runningNow != null && c.runningNow > 0) {
    out.push(`${plural(c.runningNow, "session")} running now keep their start rate.`);
  }
  if (c.notesLast30DaysLonger != null && c.notesLast30DaysLonger > 0) {
    out.push(`${plural(c.notesLast30DaysLonger, "voice note")} in the last 30 days ran longer.`);
  }
  // The server's own sentence, unless it names a total this Save replaces.
  // Prices carry PRICE_KEEP_NOTE instead (the same fact, shorter).
  if (view.note && !isPriceKey(key) && !(key === "FREE_PLAN_RESET_MODE" && opts.freeTotal)) out.push(view.note);
  return out;
}

/** Save waits while a count is still coming; a missing endpoint or an error never blocks. */
export function impactsPending(views: (ImpactView | undefined)[]): boolean {
  return views.some((v) => !v || v.status === "loading");
}
